import * as THREE from 'three';
import type { Object3D } from 'three';
import {
	isSketchArcObject,
	isSketchCircleObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type { SketchArcObject } from '../../../mesh/sketch/SketchArcObject';
import type { SketchCircleObject } from '../../../mesh/sketch/SketchCircleObject';
import type { SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import type { SketchLineObject } from '../../../mesh/sketch/SketchLineObject';
import type { SketchParabolaObject } from '../../../mesh/sketch/SketchParabolaObject';
import type { SketchSplineObject } from '../../../mesh/sketch/SketchSplineObject';
import type { SketchStyleSplineObject } from '../../../mesh/sketch/SketchStyleSplineObject';
import type {
	SketchArcTargetSnapshot,
	SketchCircleTargetSnapshot,
	SketchEllipseTargetSnapshot,
	SketchEntityReferenceTargetSnapshot,
	SketchLineTargetSnapshot,
	SketchParabolaTargetSnapshot,
	SketchSplineTargetSnapshot,
	SketchStyleSplineTargetSnapshot,
	WorldCoordinate,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceTargetSnapshotService';

export type { SketchEntityReferenceTargetSnapshot } from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceTargetSnapshotService';

/**
 * 草图实体引用目标快照服务。
 * 说明：
 * 1. 该服务只负责“捕获当前目标实体几何状态”和“在重建失败时恢复”；
 * 2. 它复用当前草图对象已有的 definition / 静默写回 API，不引入额外缓存结构；
 * 3. 这样可以对齐参考分支里 `captureTargetSnapshot -> restoreTargetSnapshot` 的失败收口顺序，
 *    同时保持当前 `projectionResultKind` 架构不变。
 */
export class SketchEntityReferenceTargetSnapshotService {
	/**
	 * 捕获目标实体几何快照。
	 *
	 * @param targetEntity 目标实体。
	 * @returns 几何快照。
	 */
	public capture(targetEntity: Object3D): SketchEntityReferenceTargetSnapshot {
		if (isSketchLineObject(targetEntity)) {
			return this.captureLine(targetEntity);
		}
		if (isSketchCircleObject(targetEntity)) {
			return this.captureCircle(targetEntity);
		}
		if (isSketchArcObject(targetEntity)) {
			return this.captureArc(targetEntity);
		}
		if (isSketchEllipseObject(targetEntity)) {
			return this.captureEllipse(targetEntity);
		}
		if (isSketchParabolaObject(targetEntity)) {
			return this.captureParabola(targetEntity);
		}
		if (isSketchSplineObject(targetEntity)) {
			return this.captureSpline(targetEntity);
		}
		if (isSketchStyleSplineObject(targetEntity)) {
			return this.captureStyleSpline(targetEntity);
		}

		throw new Error('不支持为当前目标实体捕获重建快照。');
	}

	/**
	 * 恢复目标实体几何快照。
	 *
	 * @param targetEntity 目标实体。
	 * @param snapshot 已捕获快照。
	 */
	public restore(
		targetEntity: Object3D,
		snapshot: SketchEntityReferenceTargetSnapshot,
	): void {
		switch (snapshot.kind) {
			case 'line':
				if (!isSketchLineObject(targetEntity)) {
					throw new Error('目标实体与直线快照类型不匹配。');
				}
				this.restoreLine(targetEntity, snapshot);
				return;
			case 'circle':
				if (!isSketchCircleObject(targetEntity)) {
					throw new Error('目标实体与圆快照类型不匹配。');
				}
				this.restoreCircle(targetEntity, snapshot);
				return;
			case 'arc':
				if (!isSketchArcObject(targetEntity)) {
					throw new Error('目标实体与圆弧快照类型不匹配。');
				}
				this.restoreArc(targetEntity, snapshot);
				return;
			case 'ellipse':
				if (!isSketchEllipseObject(targetEntity)) {
					throw new Error('目标实体与椭圆快照类型不匹配。');
				}
				this.restoreEllipse(targetEntity, snapshot);
				return;
			case 'parabola':
				if (!isSketchParabolaObject(targetEntity)) {
					throw new Error('目标实体与抛物线快照类型不匹配。');
				}
				this.restoreParabola(targetEntity, snapshot);
				return;
			case 'spline':
				if (!isSketchSplineObject(targetEntity)) {
					throw new Error('目标实体与普通样条快照类型不匹配。');
				}
				this.restoreSpline(targetEntity, snapshot);
				return;
			case 'style-spline':
				if (!isSketchStyleSplineObject(targetEntity)) {
					throw new Error('目标实体与样式样条快照类型不匹配。');
				}
				this.restoreStyleSpline(targetEntity, snapshot);
				return;
			default: {
				const exhaustiveCheck: never = snapshot;
				throw new Error(`未支持的目标快照类型: ${(exhaustiveCheck as { kind: string }).kind}`);
			}
		}
	}

	/**
	 * 捕获直线快照。
	 *
	 * @param targetLine 目标直线。
	 * @returns 直线快照。
	 */
	private captureLine(targetLine: SketchLineObject): SketchLineTargetSnapshot {
		const startWorld = targetLine.startPoint.getPosition();
		const endWorld = targetLine.endPoint.getPosition();
		return {
			kind: 'line',
			startWorld: [startWorld.x, startWorld.y, startWorld.z],
			endWorld: [endWorld.x, endWorld.y, endWorld.z],
		};
	}

	/**
	 * 恢复直线快照。
	 *
	 * @param targetLine 目标直线。
	 * @param snapshot 直线快照。
	 */
	private restoreLine(
		targetLine: SketchLineObject,
		snapshot: SketchLineTargetSnapshot,
	): void {
		targetLine.startPoint.syncPositionSilently(
			targetLine.startPoint.getPosition().set(...snapshot.startWorld),
		);
		targetLine.endPoint.syncPositionSilently(
			targetLine.endPoint.getPosition().set(...snapshot.endWorld),
		);
		targetLine.updateGeometry();
	}

	/**
	 * 捕获圆快照。
	 *
	 * @param targetCircle 目标圆。
	 * @returns 圆快照。
	 */
	private captureCircle(targetCircle: SketchCircleObject): SketchCircleTargetSnapshot {
		const centerWorld = targetCircle.getCenterWorld();
		return {
			kind: 'circle',
			centerWorld: [centerWorld.x, centerWorld.y, centerWorld.z],
			radius: targetCircle.getRadius(),
		};
	}

	/**
	 * 恢复圆快照。
	 *
	 * @param targetCircle 目标圆。
	 * @param snapshot 圆快照。
	 */
	private restoreCircle(
		targetCircle: SketchCircleObject,
		snapshot: SketchCircleTargetSnapshot,
	): void {
		targetCircle.getCenterControlPoint().syncPositionSilently(
			targetCircle.getCenterControlPoint().getPosition().set(...snapshot.centerWorld),
		);
		targetCircle.setRadiusSilently(snapshot.radius);
	}

	/**
	 * 捕获圆弧快照。
	 *
	 * @param targetArc 目标圆弧。
	 * @returns 圆弧快照。
	 */
	private captureArc(targetArc: SketchArcObject): SketchArcTargetSnapshot {
		return {
			kind: 'arc',
			definition: targetArc.getArcDefinition(),
		};
	}

	/**
	 * 恢复圆弧快照。
	 *
	 * @param targetArc 目标圆弧。
	 * @param snapshot 圆弧快照。
	 */
	private restoreArc(
		targetArc: SketchArcObject,
		snapshot: SketchArcTargetSnapshot,
	): void {
		targetArc.updateArcDefinition(snapshot.definition);
		targetArc.syncControlPointsFromDefinition();
	}

	/**
	 * 捕获椭圆快照。
	 *
	 * @param targetEllipse 目标椭圆/椭圆弧。
	 * @returns 椭圆快照。
	 */
	private captureEllipse(targetEllipse: SketchEllipseObject): SketchEllipseTargetSnapshot {
		return {
			kind: 'ellipse',
			definition: targetEllipse.getEllipseDefinition(),
		};
	}

	/**
	 * 恢复椭圆快照。
	 *
	 * @param targetEllipse 目标椭圆/椭圆弧。
	 * @param snapshot 椭圆快照。
	 */
	private restoreEllipse(
		targetEllipse: SketchEllipseObject,
		snapshot: SketchEllipseTargetSnapshot,
	): void {
		targetEllipse.updateEllipseDefinition(snapshot.definition);
		targetEllipse.syncControlPointsFromDefinition();
	}

	/**
	 * 捕获抛物线快照。
	 *
	 * @param targetParabola 目标抛物线。
	 * @returns 抛物线定义快照。
	 */
	private captureParabola(targetParabola: SketchParabolaObject): SketchParabolaTargetSnapshot {
		return {
			kind: 'parabola',
			definition: targetParabola.getParabolaDefinition(),
		};
	}

	/**
	 * 恢复抛物线快照。
	 *
	 * `updateParabolaDefinition` 会同时刷新曲线与四个控制点，避免恢复后定义和场景点位分离。
	 *
	 * @param targetParabola 目标抛物线。
	 * @param snapshot 抛物线定义快照。
	 */
	private restoreParabola(
		targetParabola: SketchParabolaObject,
		snapshot: SketchParabolaTargetSnapshot,
	): void {
		targetParabola.updateParabolaDefinition(snapshot.definition);
	}

	/**
	 * 捕获普通样条快照。
	 *
	 * @param targetSpline 目标普通样条。
	 * @returns 普通样条快照。
	 */
	private captureSpline(targetSpline: SketchSplineObject): SketchSplineTargetSnapshot {
		return {
			kind: 'spline',
			controlPointWorlds: targetSpline.getControlPoints()
				.map((point) => toWorldCoordinate(point.getPosition())),
			curvePointWorlds: targetSpline.getCurvePoints()
				.map(toWorldCoordinate),
			curveMetadata: targetSpline.getCurveMetadata(),
		};
	}

	/**
	 * 恢复普通样条快照。
	 *
	 * @param targetSpline 目标普通样条。
	 * @param snapshot 普通样条快照。
	 */
	private restoreSpline(
		targetSpline: SketchSplineObject,
		snapshot: SketchSplineTargetSnapshot,
	): void {
		this.restoreSplineLike(targetSpline, snapshot);
	}

	/**
	 * 捕获样式样条快照。
	 *
	 * @param targetSpline 目标样式样条。
	 * @returns 样式样条快照。
	 */
	private captureStyleSpline(targetSpline: SketchStyleSplineObject): SketchStyleSplineTargetSnapshot {
		return {
			kind: 'style-spline',
			controlPointWorlds: targetSpline.getControlPoints()
				.map((point) => toWorldCoordinate(point.getPosition())),
			curvePointWorlds: targetSpline.getCurvePoints()
				.map(toWorldCoordinate),
			curveMetadata: targetSpline.getCurveMetadata(),
		};
	}

	/**
	 * 恢复样式样条快照。
	 *
	 * @param targetSpline 目标样式样条。
	 * @param snapshot 样式样条快照。
	 */
	private restoreStyleSpline(
		targetSpline: SketchStyleSplineObject,
		snapshot: SketchStyleSplineTargetSnapshot,
	): void {
		this.restoreSplineLike(targetSpline, snapshot);
	}

	/**
	 * 恢复普通样条/样式样条的共享实现。
	 * 说明：
	 * 1. 控制点数量不变时静默写点并刷新采样点，避免普通样条触发 Worker；
	 * 2. 控制点数量变化时回退到对象自身完整重建协议；
	 * 3. 快照只用于失败回滚，不应额外制造异步副作用。
	 *
	 * @param targetSpline 目标样条。
	 * @param snapshot 样条快照。
	 */
	private restoreSplineLike(
		targetSpline: SketchSplineObject | SketchStyleSplineObject,
		snapshot: SketchSplineTargetSnapshot | SketchStyleSplineTargetSnapshot,
	): void {
		const controlPointPositions = snapshot.controlPointWorlds.map(toVector3);
		const curvePoints = snapshot.curvePointWorlds.map(toVector3);
		const currentControlPoints = targetSpline.getControlPoints();
		if (currentControlPoints.length === controlPointPositions.length) {
			currentControlPoints.forEach((point, index) => {
				point.syncPositionSilently(controlPointPositions[index].clone());
			});
			targetSpline.setCurveMetadata(snapshot.curveMetadata);
			targetSpline.updateCurvePoints(curvePoints);
			return;
		}

		targetSpline.rebuildByCurveDefinition({
			controlPointPositions,
			curvePoints,
			curveMetadata: snapshot.curveMetadata,
		});
	}
}

/**
 * 将 Vector3-like 点转为世界坐标元组。
 *
 * @param point 世界坐标点。
 * @returns 世界坐标元组。
 */
function toWorldCoordinate(point: { x: number; y: number; z: number }): WorldCoordinate {
	return [point.x, point.y, point.z];
}

/**
 * 将世界坐标元组转为向量。
 *
 * @param point 世界坐标元组。
 * @returns 三维向量。
 */
function toVector3(point: WorldCoordinate): THREE.Vector3 {
	return new THREE.Vector3(point[0], point[1], point[2]);
}
